import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import type { PbjTerminatedEmployeeRow } from "../../../utils/models";
import { usePbjTerminated } from "../hooks/usePbjAudit";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { boldNameColumn, hoursColumn, monospaceIdColumn } from "../pbjGridCells";
import { formatAuditDate } from "../pbjGridFormat";

type Props = {
	startDate: string;
	endDate: string;
};

/** Unique grid id — fold in the array index so equal (emp, termination-date) rows never collide. */
type TerminatedGridRow = PbjTerminatedEmployeeRow & { __rowId: string };

/**
 * Terminated employees audit — staff terminated within (or before) the window with their worked hours.
 * Migrated to the canonical SystemDataGrid (opaque pinned header, skeleton loader, internal scroll).
 */
export const TerminatedTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const { data, isLoading, isError } = usePbjTerminated(startDate, endDate);

	const rows = useMemo<TerminatedGridRow[]>(() => (data ?? []).map((r, i) => ({ ...r, __rowId: `${i}|${r.empId}|${String(r.terminationDate)}` })), [data]);

	const columns: GridColDef<TerminatedGridRow>[] = useMemo(
		() => [
			monospaceIdColumn<TerminatedGridRow>("empId", t("pbj_model.audit.terminated.cols.id")),
			boldNameColumn<TerminatedGridRow>("name", t("pbj_model.audit.terminated.cols.name")),
			{
				field: "jobDescription",
				headerName: t("pbj_model.audit.terminated.cols.job"),
				minWidth: 160,
				flex: 1,
				valueFormatter: (value: string | null) => value || "—",
			},
			{
				field: "terminationDate",
				headerName: t("pbj_model.audit.terminated.cols.term_date"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			hoursColumn<TerminatedGridRow>("totalHours", t("pbj_model.audit.terminated.cols.total_hours"), { minWidth: 120 }),
			{
				field: "lastShiftWorked",
				headerName: t("pbj_model.audit.terminated.cols.last_shift"),
				minWidth: 140,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
		],
		[t],
	);

	return (
		<SettingsSection
			layout="stack"
			title={t("pbj_model.audit.terminated.section.title")}
			description={t("pbj_model.audit.terminated.section.subtitle")}
			dataTestId="pbj-terminated-section"
			sx={(theme) => sectionCardSx(theme)}
		>
			<SystemDataGrid<TerminatedGridRow>
				rows={rows}
				columns={columns}
				getRowId={(r) => r.__rowId}
				searchableColumns={["empId", "name"]}
				searchPlaceholder={t("pbj_model.audit.terminated.search")}
				loading={isLoading}
				error={isError}
				errorMessage={t("pbj_model.audit.terminated.failed")}
				emptyState={<EmptyState title={t("pbj_model.audit.terminated.empty")} dataTestId="pbj-model-audit-terminated-empty" />}
				defaultPageSize={25}
				containedHeight
				sx={(theme) => dataTableSx(theme)}
				dataTestId="pbj-model-audit-terminated"
			/>
		</SettingsSection>
	);
};
